import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  MapPin, 
  Building2,
  Tag,
  Clock,
  Settings
} from 'lucide-react'
import { Button, Badge, Card, CardHeader, CardBody, ConfirmModal } from '../../components/ui'
import type { Store } from '../../types'

// Mock data
const mockStore: Store = {
  id: '1',
  companyId: 'comp-1',
  code: 'SHP001',
  name: '渋谷道玄坂店',
  latitude: 35.659517,
  longitude: 139.698962,
  prefecture: '東京都',
  city: '渋谷区',
  address: '道玄坂2-1-1 渋谷ビル3F',
  timezone: 'Asia/Tokyo',
  status: 'active',
  createdAt: '2024-01-15',
  updatedAt: '2025-12-01',
  groups: [
    { id: 'g1', name: '関東地区', groupType: 'region', createdAt: '' },
    { id: 'g2', name: '大型店舗', groupType: 'size', createdAt: '' },
    { id: 'g5', name: 'プレミアム契約', groupType: 'contract', createdAt: '' },
  ],
}

const statusConfig = {
  active: { label: '稼働中', variant: 'success' as const },
  inactive: { label: '停止中', variant: 'danger' as const },
  maintenance: { label: 'メンテナンス', variant: 'warning' as const },
}

export default function StoreDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'info' | 'ads' | 'stats'>('info')

  const store = mockStore
  const status = statusConfig[store.status]

  const handleDelete = () => {
    console.log('Delete store:', id)
    setShowDeleteModal(false)
    navigate('/stores')
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/stores" className="breadcrumb-item flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" />
          店舗一覧
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="text-gray-900">{store.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{store.name}</h1>
            <Badge variant={status.variant} dot>{status.label}</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1 font-mono">{store.code}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/stores/${id}/edit`}>
            <Button variant="secondary" leftIcon={<Edit className="w-4 h-4" />}>
              編集
            </Button>
          </Link>
          <Button variant="danger" leftIcon={<Trash2 className="w-4 h-4" />} onClick={() => setShowDeleteModal(true)}>
            削除
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'info', label: '基本情報' },
            { id: 'ads', label: '広告設定' },
            { id: 'stats', label: '配信実績' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-gray-400" />
                  店舗情報
                </h3>
              </CardHeader>
              <CardBody>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">店舗コード</dt>
                    <dd className="mt-1 text-gray-900 font-mono">{store.code}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">店舗名</dt>
                    <dd className="mt-1 text-gray-900">{store.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">ステータス</dt>
                    <dd className="mt-1">
                      <Badge variant={status.variant} dot>{status.label}</Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">タイムゾーン</dt>
                    <dd className="mt-1 text-gray-900">{store.timezone}</dd>
                  </div>
                </dl>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-gray-400" />
                  所在地
                </h3>
              </CardHeader>
              <CardBody>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">都道府県</dt>
                    <dd className="mt-1 text-gray-900">{store.prefecture}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">市区町村</dt>
                    <dd className="mt-1 text-gray-900">{store.city}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm text-gray-500">住所</dt>
                    <dd className="mt-1 text-gray-900">{store.address}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">緯度</dt>
                    <dd className="mt-1 text-gray-900 font-mono">{store.latitude}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">経度</dt>
                    <dd className="mt-1 text-gray-900 font-mono">{store.longitude}</dd>
                  </div>
                </dl>
                
                {/* Map placeholder */}
                <div className="mt-4 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <MapPin className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">地図表示エリア</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <Tag className="w-5 h-5 mr-2 text-gray-400" />
                  所属グループ
                </h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-2">
                  {store.groups?.map(group => (
                    <div key={group.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-900">{group.name}</span>
                      <Badge variant="gray">
                        {group.groupType === 'region' ? '地域' : group.groupType === 'size' ? '規模' : '契約'}
                      </Badge>
                    </div>
                  ))}
                </div>
                <Button variant="secondary" size="sm" className="w-full mt-4">
                  グループを編集
                </Button>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-gray-400" />
                  更新履歴
                </h3>
              </CardHeader>
              <CardBody>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm text-gray-500">作成日</dt>
                    <dd className="text-gray-900">{new Date(store.createdAt).toLocaleDateString('ja-JP')}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">最終更新</dt>
                    <dd className="text-gray-900">{new Date(store.updatedAt).toLocaleDateString('ja-JP')}</dd>
                  </div>
                </dl>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'ads' && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-gray-900 flex items-center">
              <Settings className="w-5 h-5 mr-2 text-gray-400" />
              広告枠別設定
            </h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-6">
              {/* Main Banner */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900">メインバナー (728x280)</h4>
                    <p className="text-sm text-gray-500">画面中央上部のメインバナー枠</p>
                  </div>
                  <Badge variant="success" dot>有効</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">固定広告:</span>
                    <span className="ml-2 text-gray-900">なし（自動配信）</span>
                  </div>
                  <div>
                    <span className="text-gray-500">ブロックリスト:</span>
                    <span className="ml-2 text-gray-900">なし</span>
                  </div>
                </div>
              </div>

              {/* Square Banner */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900">正方形バナー (300x250)</h4>
                    <p className="text-sm text-gray-500">画面右側の正方形バナー枠</p>
                  </div>
                  <Badge variant="success" dot>有効</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">固定広告:</span>
                    <span className="ml-2 text-gray-900">カフェプロモーション</span>
                  </div>
                  <div>
                    <span className="text-gray-500">期間:</span>
                    <span className="ml-2 text-gray-900">12/1 〜 12/15</span>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {activeTab === 'stats' && (
        <Card>
          <CardBody className="text-center py-12">
            <p className="text-gray-500">配信実績機能は準備中です</p>
          </CardBody>
        </Card>
      )}

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="店舗を削除"
        message="この店舗を削除しますか？関連する配信設定も削除されます。"
        confirmText="削除する"
        variant="danger"
      />
    </div>
  )
}

